import React, { useState, useEffect } from "react";
import { GlobalService } from "./../../shared";
import Card from "../../../components/ui/card";
import { CardHeader, CardTitle, CardContent } from "../../../components/ui/card";
import { Eye, EyeOff, ArrowLeft, Mail, Lock, UserRound, CircleAlert } from "lucide-react";
import { motion } from "framer-motion";
import { ResetPassword, useAppDispatch } from "../../../redux";
import { ResetErrorsType } from "../../shared/model/AuthInterfaces";
import CommonLoginLeftPart from "./CommonLoginLeftPart";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Spinner from "../../shared/components/Spinner";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/LanguageSelector";

const ResetPasswordComponent = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        hash: "",
        password: "",
        confirmPassword: "",
    });
    const isLoading = useSelector(selectLoaderValue);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<ResetErrorsType>({});
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const hashFromURL = queryParams.get("hash");

        if (hashFromURL) {
            setFormData(prev => ({
                ...prev,
                hash: hashFromURL,
            }));
        }
    }, [location.search]);

    const validate = () => {
        const newErrors: ResetErrorsType = {};

        if (!formData.password || formData.password.length < 8)
            newErrors.password = t("resetPassword.passwordRequired");
        if (formData.confirmPassword !== formData.password) newErrors.confirmPassword = t("resetPassword.passwordsNotMatch");

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async e => {
        if (!isLoading) {
            e.preventDefault();
            if (validate()) {
                const { confirmPassword, ...payload } = formData;
                try {
                    await dispatch(ResetPassword(payload)).unwrap();
                    navigate("/auth/login"); // ✅ do this here
                } catch (err) {
                    throw err;
                }
            }
        } else {
            GlobalService.showToast("info", t("resetPassword.pleaseWait"), t("resetPassword.processingRequest"));
        }
    };

    const handleChange = e => {
        const { name, type, checked, value } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-100 flex flex-col p-4">
            {/* Navigation Header */}
            <div className="max-w-7xl mx-auto w-full py-6 px-4">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between">
                    <Link
                        to={"/"}
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                        <span>{t("resetPassword.backToHome")}</span>
                    </Link>
                    <LanguageSelector />
                </motion.div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8 items-center p-4">
                    {/* Left Side - Information */}
                    <CommonLoginLeftPart />

                    {/* Right Side - Login Form */}

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}>
                        <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur" color={undefined}>
                            <CardHeader className="pb-6" color={undefined}>
                                <div className="md:hidden  flex justify-center">
                                    <div className="text-2xl font-extrabold text-blue-600 flex items-center gap-3">
                                        <img
                                            src="https://assests.netlify.app/assets/images/logo.png"
                                            alt="Kokobeo Logo"
                                            className="w-10 h-10 object-contain"
                                        />
                                        <span>Kokobeo</span>
                                    </div>
                                </div>
                                <CardTitle className="text-2xl font-bold text-center text-gray-900" color={undefined}>
                                    {t("resetPassword.title")}
                                </CardTitle>
                                <p className="mt-2 text-sm text-gray-600 text-center">
                                    {t("resetPassword.subtitle")}
                                </p>
                            </CardHeader>
                            {Object.keys(errors).length > 0 && (
                                <div className="w-full flex justify-center">
                                    <div className="w-[88%]  bg-red-100 rounded-md px-3 py-2 flex items-center">
                                        <CircleAlert className="text-red-500 w-4 h-4" />
                                        <p className="text-red-600 text-sm pl-2">{String(Object.values(errors)[0])}</p>
                                    </div>
                                </div>
                            )}
                            <CardContent className="space-y-4">
                                {/* Divider */}
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-4 text-gray-500 bg-white">{t("resetPassword.orContinueWith")}</span>
                                    </div>
                                </div>
                                {/* Password Field */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                            {t("resetPassword.password")}
                                        </label>
                                    </div>
                                    <div className="relative">
                                        <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder={t("resetPassword.passwordPlaceholder")}
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-12 text-sm 
                        text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                                        />
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                                            {showPassword ? (
                                                <EyeOff className="h-5 w-5" />
                                            ) : (
                                                <Eye className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                            {t("resetPassword.confirmPassword")}
                                        </label>
                                    </div>
                                    <div className="relative">
                                        <input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="h-12 w-full rounded-lg border border-gray-300 pl-11 pr-12 text-sm 
                        text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                                        />
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                                            {showConfirmPassword ? (
                                                <EyeOff className="h-5 w-5" />
                                            ) : (
                                                <Eye className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                {/* Login Button */}
                                <motion.button
                                    onClick={handleSubmit}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="w-full h-12 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                    transition-colors font-medium flex items-center justify-center gap-2">
                                    {isLoading ? (
                                        <>
                                            <Spinner size={5} />
                                            {t("resetPassword.loading")}
                                        </>
                                    ) : (
                                        t("resetPassword.resetButton")
                                    )}{" "}
                                </motion.button>

                                {/* Registration Link */}
                                <p className="text-sm text-center text-gray-600">
                                    {t("resetPassword.noAccount")}{" "}
                                    <Link to={"/auth/register"}>
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
                                            {t("resetPassword.signUp")}
                                        </motion.button>
                                    </Link>
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordComponent;
